import {
  Alert,
  Autocomplete,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { toggleEnrollModal } from "app/store/nonCompliantPatient/slice/nonCompliantPatientSlice";
import { useEnrollNonEnrolledPatientMutation } from "app/store/patient/api/patientApi";
import GridListItem from "components/common/grid/GridListItem";

import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import dayjs from "dayjs";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { enrollFormValidationSchema } from "schemas/enrollFormValidationSchema";
import { subscribeSettingMapper } from "static/constants";
import { DEFAULT_DATE_FORMAT } from "static/date";
import { formatDateTime, getFormattedDate } from "utils/date";
import { showErrorToast, showSuccessToast } from "utils/toast";
import states from "components/pages/transmissions/modal/patient/static/states";
import { validateEnrollPatient } from "./enrollPatientValidator";
import PhoneMaskInput from "components/common/input/PhoneMaskInput";
import { searchPatients } from "app/services/patient/patient";

const EnrollPatientModal = ({ devices }) => {
  const dispatch = useDispatch();

  const [duplicatePatients, setDuplicatePatients] = useState([]);

  const { accounts } = useSelector((state) => state.account);
  const { showEnrollModal, patient } = useSelector(
    (state) => state.nonCompliantPatient
  );

  const currentAccount = accounts.find(
    (account) => account.name === patient?.account
  );

  const [enrollNonEnrolledPatient, { isLoading }] =
    useEnrollNonEnrolledPatientMutation();

  useEffect(() => {
    if (showEnrollModal && patient?.dob) {
      checkPatientExists(patient.dob);
    }
  }, [patient]);

  const checkPatientExists = async (dob) => {
    try {
      const [lastName, firstName] = patient?.name?.split(",") || [];
      const payload = {
        dob: formatDateTime(dob) || "",
        lastName: lastName?.trim() || "",
        firstName: firstName?.trim() || "",
      };

      if (!firstName || !lastName || !dob) {
        return;
      }

      const response = await searchPatients(payload);

      setDuplicatePatients(response?.data || []);
    } catch (error) {
      showErrorToast(error?.message);
    }
  };

  const handleEnroll = async (values) => {
    try {
      const { modelName, serialNumber, lastSend, ...rest } = patient;

      const hasPhoneNumber = Boolean(values?.phone);
      const hasFullAddress =
        values?.street && values?.city && values?.state && values?.zip;

      const payload = {
        ...rest,
        ...values,
        serial_number: serialNumber,
        last_send: lastSend,
        next_send: hasPhoneNumber && Boolean(values?.next_send),
        first_call: hasPhoneNumber && Boolean(values?.first_call),
        second_call: hasPhoneNumber && Boolean(values?.second_call),
        third_call: hasPhoneNumber && Boolean(values?.third_call),
        letter: hasFullAddress ? Boolean(values?.letter) : false,
      };

      const response = await enrollNonEnrolledPatient(payload).unwrap();

      if (!response?.error) {
        showSuccessToast(response?.message);
        dispatch(toggleEnrollModal({ showModal: false }));
      }
    } catch (error) {
      showErrorToast(error?.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      model_name: patient?.modelName
        ? devices?.find(
            (item) =>
              item?.modelDisplayName === patient?.modelName ||
              item?.modelName === patient?.modelName
          )?.modelDisplayName
        : null,
      doi: patient?.doi ? formatDateTime(patient.doi) : null,
      dob: patient?.dob ? formatDateTime(patient.dob) : null,
      phone: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      next_send: false,
      first_call: false,
      second_call: false,
      third_call: false,
      letter: false,
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: enrollFormValidationSchema,
    validate: (values) =>
      validateEnrollPatient(
        values,
        currentAccount?.settings?.subscribe_setting || {}
      ),
    onSubmit: (values) => handleEnroll(values),
  });

  const {
    values,
    setFieldValue,
    touched,
    errors,
    handleSubmit,
    resetForm,
    isSubmitting,
  } = formik;

  const handleCancel = () => {
    dispatch(toggleEnrollModal({ showModal: false }));
    resetForm();
  };

  return (
    <ModalWrapper
      show={showEnrollModal}
      title="Enroll Patient"
      showContentDivider
      maxWidth="md"
      onClose={() => handleCancel()}
      onCancel={() => handleCancel()}
      onConfirm={(e) => {
        handleSubmit(e);
      }}
      actionButtons={[
        {
          label: "Cancel",
          variant: "outlined",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Enroll Patient",
          onClick: handleSubmit,
          disabled: isSubmitting || isLoading,
          isLoading: isSubmitting || isLoading,
        },
      ]}
    >
      <Grid item xs={12}>
        <div className="patient-form__info--header mt-2 mb-0">
          Patient Information
        </div>
      </Grid>
      <Grid item container>
        <GridListItem size="4" title="Name" subTitle={patient?.name ?? "—"} />
        <GridListItem
          size="4"
          title="Serial Number"
          subTitle={patient?.serialNumber ?? "—"}
        />
        <GridListItem
          size="4"
          title="Last Send Date"
          subTitle={
            patient?.lastSend
              ? getFormattedDate(patient?.lastSend, DEFAULT_DATE_FORMAT)
              : "—"
          }
        />
        <GridListItem
          size="4"
          title="Account"
          subTitle={patient?.account ?? "—"}
        />
        <GridListItem
          size="4"
          title="Manufacturer"
          subTitle={patient?.manufacturer ?? "—"}
        />
      </Grid>

      <Divider className="mt-2" />

      {duplicatePatients?.length > 0 && (
        <Alert severity="warning" className="mb-3 mt-2">
          <div>
            Warning: One or more patients with the same name and date of birth
            already exist in the system.
            <ul
              style={{
                marginLeft: "1.5rem",
                marginTop: "0.5rem",
                listStyleType: "disc",
              }}
            >
              {duplicatePatients.map((duplicate) => (
                <li key={duplicate?.evoke_id}>
                  <b>
                    {duplicate?.name} — {duplicate?.evoke_id}
                  </b>
                </li>
              ))}
            </ul>
          </div>
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className="patient-form__info--header mt-1 mb-0">
            Personal Information
          </div>
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            inputVariant="outlined"
            autoOk
            id="filter_date"
            label="Date of Birth"
            format="MM/DD/YYYY"
            name="dob"
            KeyboardButtonProps={{
              "aria-label": "pick date",
            }}
            placeholder="MM/DD/YYYY"
            value={values.dob ? dayjs(values.dob) : null}
            onChange={(date) => {
              const formattedDate = date
                ? dayjs(date).format("MM/DD/YYYY")
                : null;

              setFieldValue("dob", formattedDate);
              checkPatientExists(formattedDate);
            }}
            readOnly={isLoading}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
              },
            }}
            maxDate={dayjs()}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className="patient-form__info--header mt-1 mb-0">
            Device Information
          </div>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <Autocomplete
              id="model_name"
              name="model_name"
              clearOnBlur
              value={
                values?.model_name
                  ? devices?.find(
                      (item) =>
                        item?.modelDisplayName === values?.model_name ||
                        item?.modelName === values?.model_name
                    )
                  : null
              }
              filterSelectedOptions
              limitTags={2}
              options={devices?.filter((item) => item?.modelDisplayName)}
              getOptionLabel={(option) => option?.modelDisplayName}
              noOptionsText="Enter to create a new model name"
              onChange={(e, newValue) => {
                const value = newValue?.modelDisplayName || "";
                setFieldValue("model_name", value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Model Name"
                  variant="outlined"
                  placeholder="Model Name"
                  required
                  InputProps={{
                    ...params.InputProps,
                    readOnly: isLoading ? true : false,
                  }}
                  error={touched.model_name && Boolean(errors.model_name)}
                  helperText={touched.model_name && errors.model_name}
                />
              )}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <DatePicker
            inputVariant="outlined"
            autoOk
            id="filter_date"
            label="Date of Implant"
            format="MM/DD/YYYY"
            name="doi"
            KeyboardButtonProps={{
              "aria-label": "pick date",
            }}
            placeholder="MM/DD/YYYY"
            value={values.doi ? dayjs(values.doi) : null}
            onChange={(date) => {
              const formattedDate = date
                ? dayjs(date).format("MM/DD/YYYY")
                : null;

              setFieldValue("doi", formattedDate);
            }}
            readOnly={isLoading}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: touched.doi && Boolean(errors?.doi),
                helperText: touched.doi && errors.doi,
              },
            }}
            maxDate={dayjs()}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className="patient-form__info--header mt-2 mb-0">
            Contact Information
          </div>
          <p
            style={{
              color: "#757575",
              fontSize: "12px",
              lineHeight: "16px",
            }}
          >
            Provide a contact method to enable subscription notification
          </p>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            variant="outlined"
            fullWidth
            label="Phone Number"
            name="phone"
            value={values?.phone}
            onChange={(event) => {
              const value = event.target.value;

              setFieldValue("phone", value);
            }}
            error={touched.phone && Boolean(errors.phone)}
            helperText={touched.phone && errors.phone}
            InputProps={{ inputComponent: PhoneMaskInput }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            variant="outlined"
            fullWidth
            label="Street Address"
            name="street"
            value={values?.street}
            onChange={(event) => {
              const value = event.target.value;

              setFieldValue("street", value);
            }}
            error={touched.street && Boolean(errors.street)}
            helperText={touched.street && errors.street}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            variant="outlined"
            fullWidth
            label="City"
            name="city"
            value={values?.city}
            onChange={(event) => {
              const value = event.target.value;

              setFieldValue("city", value);
            }}
            error={touched.city && Boolean(errors.city)}
            helperText={touched.city && errors.city}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={states}
            value={values.state || null}
            onChange={(_event, newValue) => {
              setFieldValue("state", newValue || "");
            }}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                name="state"
                error={touched.state && Boolean(errors.state)}
                helperText={touched.state && errors.state}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            variant="outlined"
            fullWidth
            label="Zip"
            name="zip"
            value={values?.zip}
            onChange={(event) => {
              const value = event.target.value;
              setFieldValue("zip", value);
            }}
            error={touched.zip && Boolean(errors.zip)}
            helperText={touched.zip && errors.zip}
          />
        </Grid>

        {errors?.phoneOrStreet && (
          <Grid item>
            <FormHelperText error>{errors?.phoneOrStreet}</FormHelperText>
          </Grid>
        )}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Divider className="mt-2 mb-2" />
          <div
            className="patient-form__info--header mb-0"
            style={{ marginTop: 15 }}
          >
            Subscribe Notification
          </div>
        </Grid>
        <Grid item>
          {subscribeSettingMapper
            ?.filter(({ value }) => value !== "subscribe")
            .map(({ label, value }, index) => (
              <Grid item key={index}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name={value}
                      checked={values?.[value]}
                      disabled={
                        value === "letter"
                          ? !(
                              values?.street &&
                              values.city &&
                              values.state &&
                              values.zip
                            )
                          : !values.phone
                      }
                      onChange={(e, c) => {
                        const fieldName = e.target.name;

                        setFieldValue(fieldName, c);
                      }}
                    />
                  }
                  label={label}
                  style={{ ".MuiCheckbox-root": { padding: "0 !important" } }}
                />
              </Grid>
            ))}
          {errors?.subscribeSetting && (
            <FormHelperText error>{errors?.subscribeSetting}</FormHelperText>
          )}
        </Grid>
      </Grid>

      <Alert
        severity="info"
        className="mt-2"
        style={{
          color: "#2196f3",
          width: "100%",
          marginLeft: 0,
          marginRight: 0,
          fontSize: "14px !important",
          lineHeight: "20px",
        }}
      >
        <b>Compliance Notice:</b> Enrolling the patient will be saved to
        Non-Compliant tab. Disabling reminders may affect appointment adherence
        tracking.
      </Alert>
    </ModalWrapper>
  );
};

export default EnrollPatientModal;
